// CS:GO-style lootbox — uses existing CSS classes from components.css
import React, { useRef, useEffect, useState, useCallback } from "react";
import imageData from "../../data/imageData";
import { playRaritySound, playRollSound } from "../../Sounds";

/* ── constants ──────────────────────────────────────────────────── */
const CARD_W     = 140;
const CARD_GAP   = 10;
const CARD_STEP  = CARD_W + CARD_GAP;
const STRIP_LEN  = 60;
const WINNER_IDX = 48;
const SPIN_MS    = 6000;

// The track has padding-left: calc(50% - 70px) in CSS, so card 0 starts centered.
// To center WINNER_IDX under the pointer, we just scroll by WINNER_IDX * CARD_STEP.
const TARGET_OFFSET = WINNER_IDX * CARD_STEP;

const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];

const RARITY_COLOR = {
  Common: "#b0b0b0", Uncommon: "#4caf50", Rare: "#2196f3",
  Epic: "#9c27b0", Legendary: "#ff9800", Mythic: "#ffeb3b", Ultra: "#ff2d78",
};

const RARITY_DRAMA = {
  Common:    { particles: 0,   shake: false, flashDuration: 0    },
  Uncommon:  { particles: 0,   shake: false, flashDuration: 0    },
  Rare:      { particles: 20,  shake: true,  flashDuration: 300  },
  Epic:      { particles: 45,  shake: true,  flashDuration: 500  },
  Legendary: { particles: 70,  shake: true,  flashDuration: 700  },
  Mythic:    { particles: 100, shake: true,  flashDuration: 900  },
  Ultra:     { particles: 140, shake: true,  flashDuration: 1200 },
};

/* ── ticker sound ───────────────────────────────────────────────── */
function playTick(pitch = 1) {
  try {
    const c   = new (window.AudioContext || window.webkitAudioContext)();
    const osc = c.createOscillator();
    const env = c.createGain();
    osc.connect(env); env.connect(c.destination);
    osc.type = "sine";
    osc.frequency.value = 900 * pitch;
    env.gain.setValueAtTime(0, c.currentTime);
    env.gain.linearRampToValueAtTime(0.09, c.currentTime + 0.004);
    env.gain.linearRampToValueAtTime(0, c.currentTime + 0.055);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.07);
    setTimeout(() => c.close(), 200);
  } catch(e) {}
}

/* ── strip builder ──────────────────────────────────────────────── */
function buildStrip(winner) {
  const pool  = [...imageData].sort(() => Math.random() - 0.5);
  const strip = Array.from({ length: STRIP_LEN }, (_, i) => pool[i % pool.length]);
  strip[WINNER_IDX] = winner;
  return strip;
}

/* ── particle canvas ────────────────────────────────────────────── */
function Particles({ color, count, active }) {
  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const colorRef   = useRef(color);
  const countRef   = useRef(count);
  colorRef.current = color;
  countRef.current = count;

  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas  = canvasRef.current;
    const ctx     = canvas.getContext("2d");
    const dpr     = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);
    const W       = canvas.offsetWidth;
    const H       = canvas.offsetHeight;
    const col     = colorRef.current;
    const cnt     = countRef.current;
    const isUltra = col === "#ff2d78";

    let parts = Array.from({ length: cnt }, (_, i) => ({
      x:     40 + Math.random() * 20,
      y:     40 + Math.random() * 20,
      angle: (i / cnt) * 360 + Math.random() * 20,
      speed: 2.5 + Math.random() * 7,
      size:  isUltra ? 4 + Math.random() * 8 : 3 + Math.random() * 5,
      alpha: 1,
      color: isUltra
        ? `hsl(${Math.floor(Math.random() * 360)},100%,65%)`
        : Math.random() > 0.4 ? col : "#ffd700",
      decay: 0.012 + Math.random() * 0.01,
    }));

    let alive = true;
    function loop() {
      if (!alive) return;
      ctx.clearRect(0, 0, W, H);
      parts = parts.map(p => ({
        ...p,
        x:     p.x + Math.cos(p.angle * Math.PI / 180) * p.speed * 0.7,
        y:     p.y + Math.sin(p.angle * Math.PI / 180) * p.speed * 0.7 + 0.15,
        speed: p.speed * 0.97,
        alpha: p.alpha - p.decay,
        size:  p.size * 0.993,
      })).filter(p => p.alpha > 0);

      parts.forEach(p => {
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.fillStyle   = p.color;
        ctx.shadowColor = p.color;
        ctx.shadowBlur  = p.size * 2.5;
        ctx.beginPath();
        ctx.arc((p.x / 100) * W, (p.y / 100) * H, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      if (parts.length > 0) rafRef.current = requestAnimationFrame(loop);
    }
    loop();
    return () => { alive = false; cancelAnimationFrame(rafRef.current); };
  }, [active]); // color/count read from refs — intentionally only [active]

  if (!active) return null;
  return <canvas ref={canvasRef} className="lootbox-particle-canvas" />;
}

/* ── main component ─────────────────────────────────────────────── */
export default function SpinWheel({ targetCard: targetCardProp, targetRarity, isDupe, inventory = [], onDone }) {
  const targetCard = targetCardProp || imageData.find(c => c.rarity === targetRarity) || imageData[0];
  const rIdx       = RARITY_ORDER.indexOf(targetCard.rarity);
  const winColor   = RARITY_COLOR[targetCard.rarity] || "#fff";
  const drama      = RARITY_DRAMA[targetCard.rarity] || RARITY_DRAMA.Common;
  const ownedNames = new Set(inventory.map(c => c.name));

  const strip    = useRef(buildStrip(targetCard)).current;
  const trackRef = useRef(null);

  const transitionEndRef = useRef(null);
  const tickIntervalRef  = useRef(null);
  const lastCardRef      = useRef(-1);
  const spinStartRef     = useRef(null);
  const landedRef        = useRef(false);

  const [phase,    setPhase]    = useState("spinning"); // spinning | landed | revealed
  const [shaking,  setShaking]  = useState(false);
  const [flashing, setFlashing] = useState(false);

  /* ── result copy ── */
  const resultCopy = (() => {
    if (isDupe) {
      if (rIdx >= 5) return { headline: "So close… again.", sub: "♻️ Duplicate — shatter it for coins" };
      if (rIdx >= 3) return { headline: "Already got one.", sub: "♻️ Duplicate — still rare though" };
      return { headline: "Seen it before.", sub: "♻️ Duplicate" };
    }
    return ({
      Common:    { headline: "A hamster.",      sub: "✨ New to your collection" },
      Uncommon:  { headline: "Not bad.",         sub: "✨ New uncommon!" },
      Rare:      { headline: "Rare pull! 💙",    sub: "✨ Nice one!" },
      Epic:      { headline: "EPIC! 💜",         sub: "✨ That's a great pull!" },
      Legendary: { headline: "LEGENDARY!! 🧡",   sub: "✨ Unreal. Absolutely unreal." },
      Mythic:    { headline: "M Y T H I C 💛",  sub: "✨ You are insane for pulling this." },
      Ultra:     { headline: "U L T R A 🌈",    sub: "✨ THE RAREST IN THE GAME. WHAT." },
    })[targetCard.rarity] || { headline: "A hamster.", sub: "✨ New!" };
  })();

  /* ── landing ── */
  const land = useCallback(() => {
    if (landedRef.current) return;
    landedRef.current = true;

    clearInterval(tickIntervalRef.current);
    playRaritySound(targetCard.rarity);
    setPhase("landed");

    if (drama.shake) {
      setShaking(true);
      setTimeout(() => setShaking(false), rIdx >= 4 ? 800 : 450);
    }
    if (rIdx >= 3) {
      setFlashing(true);
      setTimeout(() => setFlashing(false), drama.flashDuration);
    }

    setTimeout(() => setPhase("revealed"), 950);
    setTimeout(onDone, 950 + 2400);
  }, [targetCard.rarity, drama, rIdx, onDone]);

  /* ── skip: freeze the track mid-scroll then snap to winner ── */
  const handleSkip = useCallback(() => {
    if (landedRef.current) return;
    const track = trackRef.current;
    if (!track) return;

    // Remove the long-spin transitionend so it can't fire land() later
    if (transitionEndRef.current) {
      track.removeEventListener("transitionend", transitionEndRef.current);
      transitionEndRef.current = null;
    }
    clearInterval(tickIntervalRef.current);

    // Step 1: freeze the track at its current painted position
    const liveX = new DOMMatrix(getComputedStyle(track).transform).m41;
    track.style.transition = "none";
    track.style.transform  = `translateX(${liveX}px)`;

    // Step 2: force a reflow so the browser registers the freeze
    // eslint-disable-next-line no-void
    void track.offsetWidth;

    // Step 3: smooth ease to the correct winner position
    track.style.transition = "transform 0.7s cubic-bezier(0.25, 1, 0.5, 1)";
    track.style.transform  = `translateX(-${TARGET_OFFSET}px)`;

    setTimeout(land, 720);
  }, [land]);

  /* ── spin setup ── */
  useEffect(() => {
    playRollSound();

    const raf = requestAnimationFrame(() => {
      const track = trackRef.current;
      if (!track) return;

      spinStartRef.current = performance.now();
      track.style.transition = `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.8, 0.25, 1)`;
      track.style.transform  = `translateX(-${TARGET_OFFSET}px)`;

      // Tick sounds — poll the live painted position
      tickIntervalRef.current = setInterval(() => {
        const m       = new DOMMatrix(getComputedStyle(track).transform);
        const cardIdx = Math.floor(-m.m41 / CARD_STEP);
        if (cardIdx !== lastCardRef.current && cardIdx >= 0) {
          lastCardRef.current = cardIdx;
          const elapsed  = (performance.now() - spinStartRef.current) / SPIN_MS;
          const slowness = Math.max(0, elapsed - 0.6) / 0.4;
          playTick(0.8 + slowness * 0.7);
        }
      }, 16);

      const onEnd = () => {
        track.removeEventListener("transitionend", onEnd);
        transitionEndRef.current = null;
        land();
      };
      transitionEndRef.current = onEnd;
      track.addEventListener("transitionend", onEnd);
    });

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(tickIntervalRef.current);
    };
  }, [land]);

  const showParticles = phase !== "spinning" && drama.particles > 0;
  const overlayClass  = `lootbox-overlay${flashing ? " lootbox-overlay-glow" : ""}`;
  const modalClass    = `lootbox-modal${shaking ? " lootbox-shake" : ""}${flashing ? " lootbox-flash" : ""}`;

  return (
    <div
      className={overlayClass}
      style={flashing ? { "--glow-rgb": hexToRgb(winColor) } : undefined}
      onClick={handleSkip}
    >
      <Particles color={winColor} count={drama.particles} active={showParticles} />

      <div
        className={modalClass}
        style={{
          ...(flashing ? { "--flash-color": winColor } : {}),
          boxShadow: phase !== "spinning"
            ? `0 0 80px ${winColor}44, 0 0 160px ${winColor}22, 0 32px 80px rgba(0,0,0,0.7)`
            : undefined,
          borderColor: phase !== "spinning" ? `${winColor}55` : undefined,
          transition: "box-shadow 0.5s ease, border-color 0.5s ease",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* header */}
        <div className="lootbox-header">
          <span className="lootbox-title">
            {phase === "spinning" ? "🎲 Rolling…" : phase === "landed" ? "✦ Landing…" : `✨ ${targetCard.rarity}!`}
          </span>
          {phase === "spinning" && <span className="lootbox-skip">tap anywhere to skip</span>}
        </div>

        {/* strip viewport */}
        <div className="lootbox-viewport">
          <div
            className="lootbox-center-line"
            style={phase !== "spinning" ? {
              background: winColor,
              boxShadow: `0 0 20px ${winColor}, 0 0 40px ${winColor}66`,
            } : undefined}
          />
          <div
            className="lootbox-center-glow"
            style={phase !== "spinning" ? {
              background: `radial-gradient(ellipse at center, ${winColor}22 0%, transparent 75%)`,
            } : undefined}
          />
          <div className="lootbox-fade-left" />
          <div className="lootbox-fade-right" />

          <div ref={trackRef} className="lootbox-track">
            {strip.map((card, i) => {
              const col   = RARITY_COLOR[card.rarity] || "#fff";
              const isWin = i === WINNER_IDX;
              const owned = ownedNames.has(card.name) || isWin;
              const won   = isWin && phase !== "spinning";
              return (
                <div
                  key={i}
                  className={`lootbox-card${won ? " lootbox-card-winner" : ""}${!owned ? " lootbox-card-locked-card" : ""}`}
                  style={{
                    borderTopColor: owned ? col : "rgba(255,255,255,0.08)",
                    boxShadow: won ? `0 0 50px ${col}aa, 0 0 20px ${col}66, inset 0 0 30px ${col}22` : undefined,
                    transform:  won ? "scale(1.08)" : undefined,
                    background: won ? `linear-gradient(160deg, ${col}18 0%, var(--bg-panel) 60%)` : undefined,
                    transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease, background 0.35s ease",
                  }}
                >
                  <div className="lootbox-card-img-wrap">
                    <img
                      src={card.image}
                      alt={card.name}
                      className="lootbox-card-img"
                      style={{
                        filter:  owned ? undefined : "grayscale(1) brightness(0.12)",
                        opacity: owned ? 1 : 0.45,
                        transform: won ? "scale(1.05)" : undefined,
                        transition: "transform 0.35s ease",
                      }}
                    />
                    {!owned && (
                      <div className="lootbox-card-locked-overlay">
                        <span className="lootbox-lock-icon">🔒</span>
                        <span className="lootbox-lock-text">Locked</span>
                      </div>
                    )}
                  </div>
                  <div className="lootbox-card-name" style={{ color: owned ? "var(--text-main)" : "rgba(255,255,255,0.18)" }}>
                    {owned ? card.name.replace(" Hamster","") : "???"}
                  </div>
                  <div className="lootbox-card-rarity" style={{ color: owned ? col : "rgba(255,255,255,0.12)" }}>
                    {owned ? card.rarity : "???"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* fixed-height result area — prevents modal resize */}
        <div style={{ minHeight: 190, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", padding: "10px 0" }}>
          {phase === "revealed" ? (
            <div className={`lootbox-result lootbox-result-${targetCard.rarity.toLowerCase()}`} style={{ "--win-color": winColor }}>
              <div className="lootbox-result-img-wrap" style={{ position: "relative" }}>
                <img
                  src={targetCard.image}
                  alt={targetCard.name}
                  className="lootbox-result-img"
                  style={{ borderColor: winColor, boxShadow: `0 0 30px ${winColor}99, 0 0 60px ${winColor}44` }}
                />
                {/* pulsing glow ring */}
                <div style={{
                  position: "absolute", inset: -6,
                  borderRadius: 18,
                  border: `2px solid ${winColor}66`,
                  boxShadow: `0 0 20px ${winColor}44`,
                  pointerEvents: "none",
                  animation: "lbRingPulse 1.5s ease-in-out infinite",
                }} />
              </div>
              <div className="lootbox-result-name">{targetCard.name}</div>
              <div className="lootbox-result-rarity" style={{ color: winColor, textShadow: `0 0 20px ${winColor}` }}>
                ✦ {targetCard.rarity}
              </div>
              <div className="lootbox-result-headline" style={{ color: isDupe ? "var(--text-muted)" : winColor }}>
                {resultCopy.headline}
              </div>
              <div className="lootbox-result-sub">{resultCopy.sub}</div>
            </div>
          ) : (
            <div className="lootbox-landing-dots" style={{ display: "flex", gap: 7 }}>
              {[0,1,2].map(i => (
                <span
                  key={i}
                  style={{
                    width: 9, height: 9, borderRadius: "50%", display: "inline-block",
                    background: phase === "landed" ? winColor : "var(--text-muted)",
                    boxShadow:  phase === "landed" ? `0 0 10px ${winColor}` : undefined,
                    animation: "lbDotBounce 1s ease-in-out infinite",
                    animationDelay: `${i * 0.14}s`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
a
      <style>{`
        @keyframes lbRingPulse {
          0%,100% { opacity: 0.5; transform: scale(1);    }
          50%     { opacity: 1;   transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}

/* ── helper: "#rrggbb" → "r,g,b" for CSS custom properties ── */
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}